import { useEffect, useRef } from 'react'

import { usersApi } from '@/api/modules/users'
import { useAuth } from '@/hooks/useAuth'

/** 根据 UA 判断设备操作系统，和 bitland-next-frontend 的实现保持一致。 */
function getOS(): string {
  if (typeof navigator === 'undefined') {
    return 'unknown'
  }
  const ua = navigator.userAgent
  if (/iPad|iPhone|iPod/.test(ua)) {
    return 'ios'
  }
  if (/Android/i.test(ua)) {
    return 'android'
  }
  if (/Windows/i.test(ua)) {
    return 'windows'
  }
  if (/Mac OS/i.test(ua)) {
    return 'macos'
  }
  if (/CrOS/i.test(ua)) {
    return 'chromeos'
  }
  if (/Linux/i.test(ua)) {
    return 'linux'
  }
  return 'unknown'
}

/** 判断当前是否处于 PWA 独立运行模式（已被「添加到主屏幕」/「安装」）。 */
function isPwaMode(): boolean {
  if (typeof window === 'undefined') {
    return false
  }
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches
  const isNavigatorStandalone =
    (navigator as unknown as { standalone?: boolean }).standalone === true
  return isStandalone || isNavigatorStandalone
}

/**
 * 登录后上报设备 OS 与 PWA 安装状态。
 *
 * 触发时机与 bitland-next-frontend 的 `PwaTracker` 保持完全一致：
 * 1. 必须已登录（`isAuthenticated && user`）。
 * 2. 整个会话只上报一次（`reportedRef` 保护）。
 * 3. 若服务端已有 `clientOS` 且（非 PWA 模式或已标记 `pwaInstalled`），则跳过——
 *    避免重复推送相同数据；只有当用户首次进入 PWA 模式时才会再次补报。
 *
 * 接口：`POST /users/client-info { os, pwaInstalled }`。
 */
export function PwaTracker(): null {
  const { user, isAuthenticated } = useAuth()
  const reportedRef = useRef(false)

  useEffect(() => {
    if (!isAuthenticated || !user || reportedRef.current) {
      return
    }

    const pwa = isPwaMode()

    // 服务端已经知道 OS、且（不在 PWA 模式 或 已记录过 pwaInstalled）——无需再上报。
    if (user.clientOS && (!pwa || user.pwaInstalled)) {
      return
    }

    reportedRef.current = true
    const os = getOS()
    usersApi.reportClientInfo(os, pwa).catch(() => {
      /* 静默失败，下次会话再重试 */
    })
  }, [isAuthenticated, user])

  return null
}

export default PwaTracker
